// @flow

import _ from 'lodash'
import * as fpti from './analytics/fpti'
import matchSorter from 'match-sorter'
import {
  getDisplayPhone,
  parseAndFormatPhoneNumber,
} from '../transfer/utils/phoneUtils'
import { formatAliasPhoneNumber } from '../../../shared/phone-utils'
import {
  AUTOCOMPLETE_ITEM_TYPE,
  CONTACT_SELECTED_GROUP,
  CONTACT_SELECTION_SEARCH_TYPE,
} from 'transfer/constants'
import { isEmail, isNetworkId, isPhone } from '../../../shared/validators'

/**
 * Given a recipient's email or phone number and an array of contacts, try to find
 * the email or phone number in the list of contacts, and return that contact
 */
export function findRecipientInContacts(
  recipient: string, // is email or phone string
  contacts: Array<Object>
) {
  return contacts.find(
    ({ email = '', phones = [] }) =>
      recipient === email ||
      phones.some((phone = {}) => {
        // todo break all of this logic out into a util (that uses griffin?) *search this comment to see everywhere*
        let phoneString

        // pull out the phone_number property, which will be either object or string
        const { phone_number = {} } = phone

        // try to pull out the properties
        const { country_code = '', national_number = '' } = phone_number

        // if the properties exist, let's construct the string, else phone_number
        // is a string so just use that
        if (country_code && national_number) {
          phoneString = `+${country_code} ${national_number}`
        } else {
          phoneString = phone_number
        }

        return recipient === phoneString
      })
  )
}

/**
 * Analytics helper to track when the recipient is in the user's contacts
 */
export function trackContact(isGiftRoute: Boolean) {
  const data = isGiftRoute ? { pp_flow: 'p2p_gift' } : {}
  return fpti.trackLinkWithData(
    'linkContact',
    'main:consumer:p2p:transfer::contact',
    '',
    data
  )
}

/**
 * filter contacts by string, matching against name, email, and phone
 * @param {array} contacts
 * @param {string} searchString
 */
export function matchContacts(contacts: Array<Object>, searchString: String) {
  return matchSorter(contacts, searchString, {
    threshold: matchSorter.rankings.ACRONYM,
    keys: [
      'alternateFullName',
      'fullName',
      'email',
      'networkIdDisplayName',
      contact => getDisplayPhone(contact),
    ],
  })
}

/**
 * Filter contacts locally using matchSorter and then order alphabetically.
 * @returns {Array}
 */
type GetLocalMatchingContactsProps = {
  contacts: Array<Object>,
  searchString: string,
  isMgm: boolean,
}
export function getLocalMatchingContacts({
  contacts,
  searchString,
  isMgm = false,
}: GetLocalMatchingContactsProps = {}) {
  const trimmed = searchString && searchString.trim()
  if (!trimmed) {
    return []
  }

  const matchingContacts = _.sortBy(
    matchSorter(contacts, trimmed, {
      threshold: matchSorter.rankings.WORD_STARTS_WITH,
      keys: [
        'firstName',
        'lastName',
        'fullName',
        'networkIdDisplayName',
        'alternateFullName', // business_name
      ],
    }),
    ['firstName', 'lastName']
  )

  // Filter the unilateral contacts for MGM flow (Member Gets Member)
  if (isMgm) {
    return matchingContacts.filter(c => c.isUnilateral)
  }

  return matchingContacts
}

/**
 * Helper to build a display-ready phone number from a contact object. Useful if
 * the contact is mobile-only.
 * @param {object} contact Contact with 'phones' property.
 * @param {string} countryCode Sender's country code. e.g. US, FR, etc. Used for
 */
export function getContactPhone(
  { phones = [] }: Object,
  countryCode: string = 'US'
): string {
  // pull out the first phone numebr
  const [phone = {}] = phones

  // pull out the phone_number property, which will be either object or string
  const { phone_number = {} } = phone

  // use phone if it is a string
  if (typeof phone_number === 'string') {
    return phone_number
  }

  // try to pull out the properties
  const { country_code = '', national_number = '' } = phone_number

  // if properties exist, format a number string with them
  if (country_code && national_number) {
    return parseAndFormatPhoneNumber(
      `+${country_code} ${national_number}`,
      countryCode
    )
  }

  // this should never happen
  return ''
}

export function getContactName(contact: Object): string | Array<string> {
  const {
    firstName = '',
    lastName = '',
    alternateFullName,
    fullName,
    companyName,
    isSellerTypeContact = false,
  } = contact
  if (isSellerTypeContact && alternateFullName) {
    return alternateFullName
  }
  return shouldDisplayFirstAndLastNameSeparately(contact)
    ? [firstName, lastName]
    : fullName || alternateFullName || companyName || ''
}

// TODO: shall we move this to node layer?
export function getDisplayName(contact: Object): string | Array<string> {
  const { email = '' } = contact
  return getContactName(contact) || getContactPhone(contact) || email
}

function shouldDisplayFirstAndLastNameSeparately(contact: Object): boolean {
  const { firstName, lastName } = contact
  // check if it is latin; if not, do not show initials
  // see https://stackoverflow.com/a/30270709
  const latinReg = /^[A-z\u00C0-\u00ff\s'\.,-\/#!$%\^&\*;:{}=\-_`~()]+$/
  return (
    firstName && lastName && latinReg.test(firstName) && latinReg.test(lastName)
  )
}

/**
 * Decoarate contact with extra details (e.g. from recipient details data)
 * @param {FormattedContact} contact - contact to decorate
 * @param {RecipientDetail} recipientDetails - recipient details to add
 * @returns {DecoratedContact} decorated contact
 */
export function decorateContact(
  contact: FormattedContact = {},
  recipientDetails: RecipientDetail = {}
): DecoratedContact {
  const decoratedContact = _.cloneDeep(contact)

  const attributes = [
    'moneyPageOverPanel',
    'recipientAccountType',
    'recurringType',
    'infoNeededFromCustomer',
    'moreInfoIsNeededForAssessCapabilities',
    'isUnilateral',
    'isRestricted',
    'thumbnailUrl',
    'token',
    'paymentExperience',
    'mayCashAdvanceFeesApply',
  ]
  if (recipientDetails.hasNetworkId) {
    attributes.push(
      'hasNetworkId',
      'networkId',
      'networkIdDisplayName',
      'networkDisplayName',
      'networkDisplayAddress'
    )
  }

  attributes.forEach(attribute => {
    if (recipientDetails[attribute] !== undefined) {
      decoratedContact[attribute] = recipientDetails[attribute]
    }
  })

  return decoratedContact
}

/**
 * Get recipient from contact depending on whether or not email or phone is present that can
 * be used as alias to match against userreadserv (e.g. to pass to assess-capabilities API)
 * @param {FormattedContact} contact - contacted to get recipient from
 * @returns {string} recipient formatted to match against userreadserv
 */
export function getRecipientFromContact(contact: FormattedContact) {
  if (contact) {
    const { email, phones } = contact

    if (email) {
      return email
    }

    if (phones && phones.length > 0) {
      // use the first phone number in array of phones from contactbookmgmtserv which is
      // most likely to match with userreadserv alias (e.g. when passing to sendmoneyserv)
      const {
        phone_number: {
          country_code: countryCode,
          national_number: nationalNumber,
        } = {},
      } = phones[0]

      if (nationalNumber) {
        return formatAliasPhoneNumber({ countryCode, nationalNumber })
      }
    }
  }

  return ''
}

/**
 * Util function to get display name from contact
 * @param contact
 */
export function getDisplayNameFromContact(contact: FormattedContact) {
  const {
    hasNetworkId = false,
    networkDisplayName = '',
    alternateFullName = '',
    fullName = '',
  } = contact
  let { displayName = '' } = contact
  if (hasNetworkId && networkDisplayName) {
    displayName = networkDisplayName
  } else if (alternateFullName) {
    displayName = alternateFullName
  } else if (fullName) {
    displayName = fullName.trim()
  }
  return displayName
}

export function getContactSelectedPosition(contact: FormattedContact) {
  const contactSelectedPosition = contact.position ? contact.position : 0
  return contactSelectedPosition
}

export function getContactSelectedGroup(
  contact: FormattedContact | AutocompleteItem
) {
  let contactSelectedGroup
  if (contact.isDirect) {
    contactSelectedGroup = CONTACT_SELECTED_GROUP.YOUR_CONTACTS
  } else if (contact.type === AUTOCOMPLETE_ITEM_TYPE.PHONE_OR_EMAIL) {
    contactSelectedGroup = CONTACT_SELECTED_GROUP.PHONE_OR_EMAIL
  } else {
    contactSelectedGroup = CONTACT_SELECTED_GROUP.PEOPLE_ON_PAYPAL
  }
  return contactSelectedGroup
}

export function getContactSelectionSearchType(recipient: string) {
  let contactSearchType
  if (isNetworkId(recipient)) {
    contactSearchType = CONTACT_SELECTION_SEARCH_TYPE.PPME
  } else if (isEmail(recipient)) {
    contactSearchType = CONTACT_SELECTION_SEARCH_TYPE.EMAIL
  } else if (isPhone(recipient)) {
    contactSearchType = CONTACT_SELECTION_SEARCH_TYPE.PHONE
  } else {
    contactSearchType = CONTACT_SELECTION_SEARCH_TYPE.NAME
  }
  return contactSearchType
}
